import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { 
  Plus, Trash2, CreditCard, Calculator, Zap, Building2, Paperclip, 
  Banknote, FileText, CheckCircle, XCircle, Upload, Eye, AlertCircle,
  Mail, User, ShieldCheck, X, Download
} from "lucide-react";
import { 
  DirectDebit, DirectDebitAttachment, AdHocPayment, DirectDebitSettings
} from "../../types/direct-debit";
import { toast } from "sonner";
import { DeleteAttachmentDialog } from "./DeleteAttachmentDialog";
import { financeApi } from "../../utils/finance-api";

interface DirectDebitFormProps {
  initialData?: Partial<DirectDebit>;
  onSubmit: (data: any, files: File[]) => void;
  onCancel: () => void;
  isLoading?: boolean;
  settings: DirectDebitSettings | null;
}

export const DirectDebitForm = ({ initialData, onSubmit, onCancel, isLoading, settings }: DirectDebitFormProps) => {
  const navigate = useNavigate();
  const [existingAttachments, setExistingAttachments] = useState<DirectDebitAttachment[]>(initialData?.attachments || []);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [adHocPayments, setAdHocPayments] = useState<AdHocPayment[]>(initialData?.adHocPayments || []);
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});

  // Attachment Delete States
  const [showDeleteAttDialog, setShowDeleteAttDialog] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<{id: string, name: string} | null>(null);

  const [debitForm, setDebitForm] = useState({
    description: initialData?.description || '',
    referenceNumber: initialData?.referenceNumber || '',
    companyId: initialData?.companyId || (initialData?.company && typeof initialData.company === 'object' ? (initialData.company as any).id : ''),
    companyName: (initialData?.company && typeof initialData.company === 'object') ? (initialData.company as any).name : (initialData?.company || ''),
    department: initialData?.department || '',
    costCentre: initialData?.costCentre || '',
    assignedContact: initialData?.assignedContact || '',
    assignedContactEmail: initialData?.assignedContactEmail || '',
    frequency: (initialData?.frequency || 'monthly') as DirectDebit['frequency'],
    currency: (initialData?.currency || 'AUD') as DirectDebit['currency'],
    firstDebitAmount: initialData?.firstDebitAmount || 0,
    monthlyDebitAmount: initialData?.monthlyDebitAmount || 0,
    finalDebitAmount: initialData?.finalDebitAmount || 0,
    payeeName: initialData?.payeeName || '',
    payeeABN: initialData?.payeeABN || '',
    paymentMethod: (initialData?.paymentMethod || 'bank_account') as 'bank_account' | 'credit_card',
    bankAccountLast4: initialData?.bankAccountLast4 || '',
    creditCardLast4: initialData?.creditCardLast4 || '',
    bsb: initialData?.bsb || '',
    accountNumber: initialData?.accountNumber || '',
    bankName: initialData?.bankName || '',
    institution: initialData?.institution || '',
    firstDebitDate: initialData?.firstDebitDate || '',
    endDate: initialData?.endDate || '',
    isRecurring: initialData?.isRecurring !== undefined ? initialData.isRecurring : true,
    status: (initialData?.status || 'active') as DirectDebit['status'],
    approvalStatus: (initialData?.approvalStatus || 'pending') as DirectDebit['approvalStatus'],
    notes: initialData?.notes || '',
    subscriptionUrl: initialData?.subscriptionUrl || '',
    portalLink: initialData?.portalLink || ''
  });

  const handleInputChange = (field: string, value: any) => {
    setDebitForm(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors: Record<string, boolean> = {};
    if (!debitForm.companyId) errors.companyId = true;
    if (!debitForm.description) errors.description = true;
    if (!debitForm.monthlyDebitAmount) errors.monthlyDebitAmount = true;
    if (!debitForm.payeeName) errors.payeeName = true;
    if (!debitForm.firstDebitDate) errors.firstDebitDate = true;
    if (!debitForm.department) errors.department = true;
    if (!debitForm.assignedContact) errors.assignedContact = true;
    if (!debitForm.bankName) errors.bankName = true;

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error("Please fill in all required fields highlighted in red");
      return;
    }

    // Sanitize data: convert empty strings to null for backend compatibility
    const sanitizedData = Object.entries(debitForm).reduce((acc, [key, value]) => {
      acc[key] = value === '' ? null : value;
      return acc;
    }, {} as any);

    // Omit UI-only fields
    const { companyName, ...payload } = sanitizedData;

    onSubmit({
      ...payload,
      adHocPayments: adHocPayments.filter(p => p.isActive),
    }, selectedFiles);
  };

  const handleFileUpload = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];

    fileArray.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return;
      }
      validFiles.push(file);
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = (id: string, fileName: string) => {
    setAttachmentToDelete({ id, name: fileName });
    setShowDeleteAttDialog(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!attachmentToDelete) return;
    try {
      await financeApi.deleteAttachment(attachmentToDelete.id);
      setExistingAttachments(prev => prev.filter(att => att.id !== attachmentToDelete.id));
      toast.success("Attachment deleted successfully");
      setShowDeleteAttDialog(false);
      setAttachmentToDelete(null);
    } catch (error) {
      toast.error("Failed to delete attachment");
    }
  };

  const activeCompanies = settings?.companies?.filter(c => c && c.isActive) || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-20">
      <div className="grid grid-cols-1 gap-12">
        {/* Core Identification */}
        <div className="space-y-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">
            <CreditCard className="h-5 w-5 text-slate-700" />
            Core Identification
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className={validationErrors.companyId ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>Company *</Label>
                <Select 
                  value={debitForm.companyId} 
                  onValueChange={(value) => {
                    const selectedCompany = settings?.companies?.find(c => c && c.id === value);
                    handleInputChange('companyId', value);
                    setDebitForm(prev => ({ 
                      ...prev, 
                      companyName: selectedCompany?.name || ''
                    }));
                  }}
                >
                  <SelectTrigger className={validationErrors.companyId ? "border-red-500 bg-red-50 focus:ring-slate-900 shadow-none h-10" : "bg-slate-100 border-transparent focus:ring-slate-900 shadow-none h-10"}>
                    <SelectValue placeholder="Select company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCompanies.length > 0 ? (
                      activeCompanies.map(company => (
                        <SelectItem key={company?.id} value={company?.id || ''}>
                          {company?.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-4 text-center space-y-3">
                        <p className="text-sm text-slate-500 italic">No available companies found.</p>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          className="w-full text-xs"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/finance/direct-debit/settings?tab=companies');
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Company
                        </Button>
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={validationErrors.description ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>Description / Purpose *</Label>
                <Input 
                  className={validationErrors.description ? "border-red-500 bg-red-50 focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" : "bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"}
                  placeholder="e.g., Xero Accounting Software"
                  value={debitForm.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Reference Number</Label>
                <Input 
                  className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"
                  placeholder="e.g., XER-2024-001"
                  value={debitForm.referenceNumber}
                  onChange={(e) => setDebitForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Amount Configuration */}
        <div className="space-y-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">
            <Calculator className="h-5 w-5 text-slate-700" />
            Payment Amount Configuration
          </h3>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">First Debit Amount</Label>
                <Input 
                  className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"
                  type="number" step="0.01"
                  placeholder="0.00"
                  value={debitForm.firstDebitAmount || ''}
                  onChange={(e) => setDebitForm(prev => ({ ...prev, firstDebitAmount: parseFloat(e.target.value) || 0 }))}
                />
                <p className="text-xs text-muted-foreground mt-1">Initial payment amount (optional)</p>
              </div>
              <div className="space-y-2">
                <Label className={validationErrors.monthlyDebitAmount ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>Monthly Debit Amount *</Label>
                <Input 
                  className={validationErrors.monthlyDebitAmount ? "border-red-500 bg-red-50 focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" : "bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"}
                  type="number" step="0.01"
                  placeholder="0.00"
                  value={debitForm.monthlyDebitAmount || ''}
                  onChange={(e) => handleInputChange('monthlyDebitAmount', parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground mt-1">Regular payment amount</p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Final Debit Amount (Balloon)</Label>
                <Input 
                  className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"
                  type="number" step="0.01"
                  placeholder="0.00"
                  value={debitForm.finalDebitAmount || ''}
                  onChange={(e) => setDebitForm(prev => ({ ...prev, finalDebitAmount: parseFloat(e.target.value) || 0 }))}
                />
                <p className="text-xs text-muted-foreground mt-1">Final payment amount (optional)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Currency</Label>
                <Select value={debitForm.currency} onValueChange={(v: any) => setDebitForm(prev => ({ ...prev, currency: v }))}>
                  <SelectTrigger className="bg-slate-100 border-transparent focus:ring-slate-900 shadow-none h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Frequency *</Label>
                <Select value={debitForm.frequency} onValueChange={(v: any) => setDebitForm(prev => ({ ...prev, frequency: v }))}>
                  <SelectTrigger className="bg-slate-100 border-transparent focus:ring-slate-900 shadow-none h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                    <SelectItem value="ad-hoc">Ad-hoc</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={validationErrors.firstDebitDate ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>First Debit Date *</Label>
                <Input 
                  className={validationErrors.firstDebitDate ? "border-red-500 bg-red-50 focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" : "bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"}
                  type="date"
                  value={debitForm.firstDebitDate}
                  onChange={(e) => handleInputChange('firstDebitDate', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">End Date (Optional)</Label>
                <Input 
                  className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"
                  type="date"
                  value={debitForm.endDate}
                  onChange={(e) => setDebitForm(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Ad-hoc Monthly Payments Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">
              <Zap className="h-5 w-5 text-slate-700" />
              Ad-hoc Monthly Payments
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const newPayment: AdHocPayment = {
                  id: `adhoc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  month: new Date().getMonth() + 1,
                  year: new Date().getFullYear(),
                  amount: 0,
                  description: '',
                  isActive: true,
                  createdAt: new Date().toISOString()
                };
                setAdHocPayments(prev => [...prev, newPayment]);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Payment
            </Button>
          </div>
          
          <div className="space-y-4">
            {adHocPayments.length > 0 ? (
              <div className="space-y-3">
                {adHocPayments.map((payment) => (
                  <div key={payment.id} className="p-4 border rounded-lg bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                      <div>
                        <Label className="text-xs font-medium">Month</Label>
                        <Select 
                          value={payment.month.toString()} 
                          onValueChange={(value) => {
                            setAdHocPayments(prev => prev.map(p => 
                              p.id === payment.id ? { ...p, month: parseInt(value) } : p
                            ));
                          }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, i) => (
                              <SelectItem key={i+1} value={(i+1).toString()}>
                                {new Date(0, i).toLocaleString('en-AU', { month: 'long' })}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Year</Label>
                        <Input type="number" className="bg-white border-slate-200 focus-visible:ring-slate-900 shadow-none h-9"
                          value={payment.year}
                          onChange={(e) => {
                            setAdHocPayments(prev => prev.map(p => 
                              p.id === payment.id ? { ...p, year: parseInt(e.target.value) || new Date().getFullYear() } : p
                            ));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Amount</Label>
                        <Input type="number" step="0.01" placeholder="0.00" className="bg-white border-slate-200 focus-visible:ring-slate-900 shadow-none h-9"
                          value={payment.amount || ''}
                          onChange={(e) => {
                            setAdHocPayments(prev => prev.map(p => 
                              p.id === payment.id ? { ...p, amount: parseFloat(e.target.value) || 0 } : p
                            ));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Description</Label>
                        <Input placeholder="Note" className="bg-white border-slate-200 focus-visible:ring-slate-900 shadow-none h-9"
                          value={payment.description}
                          onChange={(e) => {
                            setAdHocPayments(prev => prev.map(p => 
                              p.id === payment.id ? { ...p, description: e.target.value } : p
                            ));
                          }}
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0"
                          onClick={() => {
                            setAdHocPayments(prev => prev.map(p => 
                              p.id === payment.id ? { ...p, isActive: !p.isActive } : p
                            ));
                          }}
                        >
                          {payment.isActive ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0"
                          onClick={() => {
                            setAdHocPayments(prev => prev.filter(p => p.id !== payment.id));
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500 border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
                <Zap className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm font-medium">No ad-hoc payments configured</p>
                <p className="text-xs text-slate-400 mt-1">Click "Add Payment" to configure specific monthly amounts</p>
              </div>
            )}
          </div>
        </div>

        {/* Organizational Information */}
        <div className="space-y-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">
            <Building2 className="h-5 w-5 text-slate-700" />
            Organizational Information
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className={validationErrors.department ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>Department *</Label>
                <Select 
                  value={debitForm.department} 
                  onValueChange={(value) => handleInputChange('department', value)}
                >
                  <SelectTrigger className={validationErrors.department ? "border-red-500 bg-red-50 focus:ring-slate-900 shadow-none h-10" : "bg-slate-100 border-transparent focus:ring-slate-900 shadow-none h-10"}>
                    <SelectValue placeholder="Select Department" />
                  </SelectTrigger>
                  <SelectContent>
                    {settings?.departments?.filter(d => d !== null).map((dept: any) => (
                      <SelectItem key={typeof dept === 'string' ? dept : (dept?.id || Math.random())} value={typeof dept === 'string' ? dept : (dept?.departmentName || '')}>
                        {typeof dept === 'string' ? dept : `${dept?.departmentCode || ''} - ${dept?.departmentName || ''}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Cost Centre</Label>
                <Input className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" placeholder="e.g., CC-001" value={debitForm.costCentre} onChange={(e) => setDebitForm(prev => ({ ...prev, costCentre: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Approval Status</Label>
                <Select value={debitForm.approvalStatus} onValueChange={(value: DirectDebit['approvalStatus']) => setDebitForm(prev => ({ ...prev, approvalStatus: value }))}>
                  <SelectTrigger className="bg-slate-100 border-transparent focus:ring-slate-900 shadow-none h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={validationErrors.assignedContact ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>Assigned Contact *</Label>
                <Input 
                  className={validationErrors.assignedContact ? "border-red-500 bg-red-50 focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" : "bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"}
                  placeholder="e.g., Sarah Johnson"
                  value={debitForm.assignedContact} 
                  onChange={(e) => handleInputChange('assignedContact', e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Contact Email</Label>
                <Input className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" type="email" placeholder="sarah.johnson@company.com" value={debitForm.assignedContactEmail} onChange={(e) => setDebitForm(prev => ({ ...prev, assignedContactEmail: e.target.value }))} />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="space-y-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">
            <Banknote className="h-5 w-5 text-slate-700" />
            Payment Details
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={validationErrors.payeeName ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>Payee / Biller Name *</Label>
                <Input 
                  className={validationErrors.payeeName ? "border-red-500 bg-red-50 focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" : "bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"}
                  placeholder="e.g., Xero (Australia) Pty Ltd"
                  value={debitForm.payeeName} 
                  onChange={(e) => handleInputChange('payeeName', e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Payee ABN</Label>
                <Input className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" placeholder="e.g., 31 143 974 616" value={debitForm.payeeABN} onChange={(e) => setDebitForm(prev => ({ ...prev, payeeABN: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={validationErrors.bankName ? "text-red-600 font-medium" : "text-slate-700 font-medium"}>
                  Bank / Institution *
                </Label>
                <Input 
                  className={validationErrors.bankName ? "border-red-500 bg-red-50 focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" : "bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10"}
                  placeholder="e.g., Commonwealth Bank, Westpac"
                  value={debitForm.bankName} 
                  onChange={(e) => handleInputChange('bankName', e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Institution Code</Label>
                <Input className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" placeholder="e.g., CBA, ANZ, WBC" value={debitForm.institution} onChange={(e) => setDebitForm(prev => ({ ...prev, institution: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Payment Method *</Label>
                <Select 
                  value={debitForm.paymentMethod} 
                  onValueChange={(v: 'bank_account' | 'credit_card') => setDebitForm(prev => ({ ...prev, paymentMethod: v }))}
                >
                  <SelectTrigger className="bg-slate-100 border-transparent focus:ring-slate-900 shadow-none h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_account">Bank Account</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">
                  {debitForm.paymentMethod === 'credit_card' ? 'Credit Card Last 4 Digits' : 'Bank Account Last 4 Digits'}
                </Label>
                <Input 
                  className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" 
                  maxLength={4} 
                  placeholder="e.g., 1234" 
                  value={debitForm.paymentMethod === 'credit_card' ? debitForm.creditCardLast4 : debitForm.bankAccountLast4} 
                  onChange={(e) => {
                    const field = debitForm.paymentMethod === 'credit_card' ? 'creditCardLast4' : 'bankAccountLast4';
                    setDebitForm(prev => ({ ...prev, [field]: e.target.value }));
                  }} 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">
            <FileText className="h-5 w-5 text-slate-700" />
            Additional Information
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Subscription URL</Label>
                <Input className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" type="url" placeholder="https://example.com/subscription" value={debitForm.subscriptionUrl} onChange={(e) => setDebitForm(prev => ({ ...prev, subscriptionUrl: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Portal Link</Label>
                <Input className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none h-10" type="url" placeholder="https://example.com/portal" value={debitForm.portalLink} onChange={(e) => setDebitForm(prev => ({ ...prev, portalLink: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Notes / Comments</Label>
              <Textarea rows={3} className="bg-slate-100 border-transparent focus-visible:ring-slate-900 focus-visible:bg-white shadow-none" placeholder="Additional notes about this direct debit..." value={debitForm.notes} onChange={(e) => setDebitForm(prev => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* Attachments Section */}
        <div className="space-y-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">
            <Paperclip className="h-5 w-5 text-slate-700" />
            Attachments
          </h3>
          
          <div className="space-y-4">
            <input 
              type="file" 
              multiple 
              className="hidden" 
              id="file-upload" 
              onChange={(e) => handleFileUpload(e.target.files!)} 
              accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg"
            />
            <div
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('file-upload')?.click();
              }}
              className={`group border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer block ${
                isDragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-400 hover:bg-slate-50/50'
              } flex flex-col items-center justify-center gap-2`}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragOver(false); handleFileUpload(e.dataTransfer.files); }}
            >
              <div className={`p-3 rounded-full transition-colors ${isDragOver ? 'bg-indigo-100' : 'bg-slate-100 group-hover:bg-indigo-50'}`}>
                <Upload className={`h-6 w-6 transition-colors ${isDragOver ? 'text-indigo-600' : 'text-slate-400 group-hover:text-indigo-600'}`} />
              </div>
              <div className="space-y-1 mb-2">
                <p className="text-sm font-bold text-slate-900">Drop files here or click to upload</p>
                <p className="text-xs text-slate-500 uppercase tracking-tight">Supported file types: PDF, XLS, DOCX, JPG. Maximum file size: 10 MB</p>
              </div>
              <Button type="button" variant="outline" className="bg-white border-slate-200 text-slate-700 h-9" onClick={(e) => {
                e.stopPropagation();
                document.getElementById('file-upload')?.click();
              }}>
                <Upload className="h-4 w-4 mr-2" />
                Choose Files
              </Button>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button type="button" variant="outline" className="bg-white border-slate-200 text-slate-700 h-9" onClick={() => document.getElementById('file-upload')?.click()}>
                <FileText className="h-4 w-4 mr-2 text-slate-500" />
                Add Authority PDF
              </Button>
              <Button type="button" variant="outline" className="bg-white border-slate-200 text-slate-700 h-9" onClick={() => document.getElementById('file-upload')?.click()}>
                <FileText className="h-4 w-4 mr-2 text-slate-500" />
                Add Contract
              </Button>
              <Button type="button" variant="outline" className="bg-white border-slate-200 text-slate-700 h-9" onClick={() => document.getElementById('file-upload')?.click()}>
                <Eye className="h-4 w-4 mr-2 text-slate-500" />
                Add Invoice
              </Button>
            </div>

            {(existingAttachments.length > 0 || selectedFiles.length > 0) && (
              <div className="mt-6 space-y-4">
                {existingAttachments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Already Attached</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {existingAttachments.map(att => (
                        <div key={att.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-white shadow-sm hover:border-indigo-100 hover:bg-indigo-50/20 transition-all duration-200 group">
                          <div className="flex items-center min-w-0 flex-1 cursor-pointer">
                            <div className="p-2 rounded bg-slate-50 mr-3 border border-slate-100 group-hover:bg-white transition-colors">
                              <FileText className="h-4 w-4 text-slate-400 group-hover:text-indigo-500" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-semibold text-slate-700 group-hover:text-indigo-600 truncate mr-2" title={att.fileName}>
                                {att.fileName}
                              </span>
                              <span className="text-[10px] text-slate-500 font-medium tracking-tighter">
                                {new Date(att.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(att.url, '_blank');
                              }}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeExistingAttachment(att.id, att.fileName);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedFiles.length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To Be Uploaded</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-white shadow-sm">
                          <div className="flex items-center min-w-0">
                            <div className="p-2 rounded bg-slate-50 mr-3 border border-slate-100">
                              <FileText className="h-4 w-4 text-slate-400" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-medium text-slate-800 truncate" title={file.name}>{file.name}</span>
                              <span className="text-[10px] text-slate-500 font-medium tracking-tighter">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50"
                            onClick={() => removeSelectedFile(idx)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-start gap-3 pt-8 mt-8 border-t border-slate-100">
          <Button type="button" variant="outline" onClick={onCancel} className="bg-white border-slate-200 text-slate-700">Cancel</Button>
          <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm ml-auto">
            <CheckCircle className="h-4 w-4 mr-2" />
            {initialData?.id ? "Update Direct Debit" : "Create Direct Debit"}
          </Button>
        </div>
      </div>

      <DeleteAttachmentDialog 
        open={showDeleteAttDialog}
        onOpenChange={setShowDeleteAttDialog}
        onConfirm={confirmDeleteAttachment}
        fileName={attachmentToDelete?.name}
      />
    </form>
  );
};
